import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { Page } from 'Common/Page/Page'
import { PageHeader } from 'Common/Supplyvue-ui'
import { CapacityView } from './Capacity/Capacity.view'
import { DashboardCardView } from './DashboardCard/DashboardCard.view'
import { HeaderElementView } from './HeaderElement/HeaderElement.view'
import { StockBuildView } from './StockBuild/StockBuild.view'
import { StockProjectionView } from './StockProjection/StockProjection.view'
import { WorkCentreDashboardPageParams } from './WorkcentreDashboard.types'
import { CapacityPage } from 'Features/Capacity/Capacity.page'
import { StockBuildPage } from 'Features/StockBuild/StockBuild.page'
import { PlanningBookView } from './PlanningBook/PlanningBook.view'
import { DashboardToggle } from './DashboardToggle/DashboardToggle.view'
import { VolumeChartView } from 'Features/VolumeChart/VolumeChart.view'
import { toast } from 'react-hot-toast'
import { useDataDownloadSettingsState } from 'Features/DownloadDataModal/DownloadDataModal.state'
import { DownloadDataModal } from 'Features/DownloadDataModal/DownloadDataModal.view'
import { usePlanBookData } from 'Features/NewDashboard/PlanningBook/PlanningBook.data'
import * as XLSX from 'xlsx'

const DISPLAY_MODES = {
  DASHBOARD_MODE: DASHBOARD_MODE_TEXT,
  PLAN_BOOK_MODE: PLAN_BOOK_MODE_TEXT,
}
import {
  DASHBOARD_MODE_TEXT,
  DASHBOARD_OPTIONS,
  PLAN_BOOK_MODE_TEXT,
} from './Dashboard.constants'
import {
  INITIAL_FILE_VERSION,
  INITIAL_FILE_FORMAT,
  FILE_NAME,
  FILE_NAME_WITH_LEDGER,
  PLANNING_BOOK_LEDGER,
  SET_KEY_FIGURES,
} from 'Features/DownloadDataModal/DownloadData.constants'
import {
  useDashboardDownload,
  usePlanningBookLedgerDownload,
  usePlanningBookLedgerKeyFigures,
} from 'Features/DownloadDataModal/DownloadDataModal.data'
import { usePlanBuiltByV2Engine } from 'Common/Charts/StockWaterfall/stockWaterfall.data'
import { getApiErrorMessage } from 'Features/auth/axios-client'
import { format } from 'date-fns'
import { DataDownloadResponse } from 'Features/DownloadDataModal/DownloadDataModal.types'
import {
  useAutoBowWave,
  useDownloadCapacity,
} from 'Features/Capacity/Capacity.data'
import { WORKCENTRE_STATUS } from 'Common/Constants/Constants'
import { StockProjectionExceptionPage } from 'Features/StockProjections/StockProjectionException.page'
import { useDashboardState } from './Dashboard.state'
import { VolumeProjectionView } from './VolumeProjection/VolumeProjection.view'
import { AlertDialog } from 'Common/Components/Dialog'
import { useWorkcentreRunStatus } from 'Features/EditCapacity/EditCapacity.data'
import { useBreadcrumbData } from 'Common/Breadcrumbs/useBreadcrumbData.hook'

export const NewWorkCentreDashboardPage = () => {
  const navigate = useNavigate()
  const { sourcesiteId, workcentreId, planId } =
    useParams<WorkCentreDashboardPageParams>()

  const breadcrumbsData = useBreadcrumbData()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dashboardDisplayState, updateDashboardState] = useDashboardState()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const constrainedPlanRunningStatus = useRef(WORKCENTRE_STATUS.UNSTARTED)

  const { data: AllplanBookData, isLoading: iskeyMeasuresLoading } =
    usePlanBookData(planId, workcentreId, isModalOpen)

  const [keyMeasures, setKeyMeasures] = useState([])

  const [dataDownloadSettingsState, updateDataDownloadSettingsState] =
    useDataDownloadSettingsState({
      planId: planId,
      workcentreId: workcentreId,
      settings: {
        fileName: 'Work Centre Outputs',
        fileVersion: INITIAL_FILE_VERSION,
        keyFigures: keyMeasures,
        fileFormat: INITIAL_FILE_FORMAT,
      },
    })

  const updateState = (newState: DataDownloadResponse) => {
    updateDataDownloadSettingsState(newState)
  }

  // "Planning Book Ledger": offered only when R3 answers 200 (the work centre
  // has a PlanningBookLedger), and asked only for plans the V2 engine built.
  const { builtByV2Engine } = usePlanBuiltByV2Engine(planId)
  const { data: ledgerKeyFigures, isSuccess: hasLedgerDownload } =
    usePlanningBookLedgerKeyFigures(
      planId,
      workcentreId,
      isModalOpen && builtByV2Engine
    )
  const ledgerCodes = useMemo(
    () => (ledgerKeyFigures ?? []).map((k) => k.measureCode),
    [ledgerKeyFigures]
  )
  const ledgerLabels = useMemo(
    () =>
      Object.fromEntries(
        (ledgerKeyFigures ?? []).map((k) => [k.measureCode, k.label])
      ),
    [ledgerKeyFigures]
  )
  const isLedgerSelected =
    dataDownloadSettingsState.fileName === PLANNING_BOOK_LEDGER

  // Picking the ledger selects all its key figures (codes); leaving it puts
  // back the key figures the other options had.
  const previousFileName = useRef(dataDownloadSettingsState.fileName)
  const keyFiguresBeforeLedger = useRef<string[]>([])
  useEffect(() => {
    const previous = previousFileName.current
    const current = dataDownloadSettingsState.fileName
    previousFileName.current = current
    if (current === PLANNING_BOOK_LEDGER && previous !== PLANNING_BOOK_LEDGER) {
      keyFiguresBeforeLedger.current = dataDownloadSettingsState.keyFigures
      updateDataDownloadSettingsState({
        type: SET_KEY_FIGURES,
        payload: ledgerCodes,
      })
    } else if (
      previous === PLANNING_BOOK_LEDGER &&
      current !== PLANNING_BOOK_LEDGER
    ) {
      updateDataDownloadSettingsState({
        type: SET_KEY_FIGURES,
        payload: keyFiguresBeforeLedger.current,
      })
    }
  }, [dataDownloadSettingsState.fileName])

  const handleDownloadData = () => {
    setIsModalOpen(true)
  }

  // Work centre run status //

  const {
    data: workcentreRunStatus,
    refetch: refetchWorkCentreRunStatus,
    isLoading: workCentreRunStatusLoading,
  } = useWorkcentreRunStatus({ planId, sourcesiteId, workcentreId })

  const { data: downloadCapacityData, isFetching: isCapacityDataDownloading } =
    useDownloadCapacity({
      sourcesiteId,
      workcentreId,
      planId: planId,
    })
  const onDownloadSuccess = (data: any) => {
    const fileName = `${
      dataDownloadSettingsState.fileName
    }__${breadcrumbsData[planId] ?? planId}__${workcentreId}__${
      dataDownloadSettingsState.fileVersion
    }__${format(new Date(), 'yyyyMMdd')}`

    if (dataDownloadSettingsState.fileFormat === 'csv') {
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${fileName}.csv`)
      document.body.appendChild(link)
      link.click()
    } else {
      const column_order = data?.column_order
      const keys = Object.keys(column_order)

      keys.sort((A, B) => column_order[A] - column_order[B])
      const reorderColumns = (data: Object[], columns: string[]) => {
        return data.map((row: any) => {
          let newRow: any = {}
          columns.forEach((column) => {
            if (row.hasOwnProperty(column)) {
              newRow[column] = row[column]
            }
          })
          Object.keys(row).forEach((column) => {
            if (!columns.includes(column)) {
              const newKey = Number.isInteger(Number(column))
                ? String(column).slice(0, 4) + '' + String(column).slice(4)
                : column
              newRow[newKey] = row[column]
            }
          })
          return newRow
        })
      }

      const orderedData = reorderColumns(data?.data, keys)

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(orderedData, { header: keys })

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')
      XLSX.writeFile(workbook, `${fileName}.xlsx`)
    }

    toast.success('Download Successful')
  }

  const onDownloadFailure = () => {
    toast.error('Download Failure')
  }

  const { mutate: downloadData, isLoading: isDataDownloading } =
    useDashboardDownload(onDownloadSuccess, onDownloadFailure)
  const { mutate: downloadLedger, isLoading: isLedgerDownloading } =
    usePlanningBookLedgerDownload(onDownloadSuccess, onDownloadFailure)

  const handleModalCancel = () => {
    setIsModalOpen(false)
  }

  const handleModalSave = () => {
    if (dataDownloadSettingsState.fileName === 'Capacity Data') {
      downloadCapacityData()
    } else if (isLedgerSelected) {
      downloadLedger({
        planId: planId as string,
        workcentreId: workcentreId as string,
        settings: dataDownloadSettingsState,
      })
    } else {
      downloadData({
        planId: planId,
        workcentreId: workcentreId,
        settings: dataDownloadSettingsState,
      })
    }

    setIsModalOpen(false)
  }

  const handleToggleButton = () => {
    if (dashboardDisplayState.mode !== DISPLAY_MODES.PLAN_BOOK_MODE) {
      updateDashboardState({
        type: 'PLAN_BOOK_MODE',
        value: DISPLAY_MODES.PLAN_BOOK_MODE,
      })
    }
  }

  const handleSelectedOption = (value: string) => {
    if (value === DASHBOARD_MODE_TEXT) {
      updateDashboardState({
        type: 'DASHBOARD_MODE',
        value: DISPLAY_MODES.DASHBOARD_MODE,
      })
    } else {
      updateDashboardState({
        type: 'SUB_MODE',
        value: value,
      })
    }
  }

  useEffect(() => {
    if (AllplanBookData) {
      const { keyMeasures: keyMeasureData } = AllplanBookData
      setKeyMeasures(keyMeasureData)
    }
  }, [AllplanBookData])

  const handleEditCapacityClick = () => {
    navigate(
      `/plan/${planId}/sourcesite/${sourcesiteId}/workcentre/${workcentreId}/edit-capacity`
    )
  }

  const handleEditSettingsClick = () => {
    navigate(`/plan/${planId}/edit`)
  }

  const onUpdateCapacitySuccess = () => {
    toast.success('Auto Bow Wave Triggered successfully')
    setShowConfirmDialog(false)
  }

  const onUpdateCapacityFailure = (error: unknown) => {
    toast.error(getApiErrorMessage(error, 'Auto Bow Wave failed to trigger'))
    setShowConfirmDialog(false)
  }

  const { mutate: updateCapacityData } = useAutoBowWave(
    onUpdateCapacitySuccess,
    onUpdateCapacityFailure
  )

  const handleBowWaveClick = () => {
    constrainedPlanRunningStatus.current = WORKCENTRE_STATUS.INITIATED
    updateCapacityData({
      planId: planId,
      workcentreId: workcentreId,
      sourcesiteId: sourcesiteId,
    })
    navigate(`/plan/${planId}`)
  }

  const handleAlternativeWorkcentres = () => {
    navigate(
      `/plan/${planId}/sourcesite/${sourcesiteId}/workcentre/${workcentreId}/alternative-workcentres`
    )
  }

  const handleBowWaveClickHandler = () => {
    refetchWorkCentreRunStatus()
    setShowConfirmDialog(true)
  }

  /***Edit production */
  const handleEditProduction = () => {
    navigate(
      `/plan/${planId}/sourcesite/${sourcesiteId}/workcentre/${workcentreId}/edit-production`
    )
  }
  /** End */

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title={dashboardDisplayState.title} />
        <Box
          display="flex"
          height="50px"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <Box>
              <Typography>Please select mode</Typography>
            </Box>
            <Box marginLeft={2}>
              <DashboardToggle
                options={Object.values(DASHBOARD_OPTIONS)}
                handleSelectedOption={handleSelectedOption}
                handleToggleButton={handleToggleButton}
              />
            </Box>
          </Box>
          <Box>
            <HeaderElementView
              planId={planId as string}
              workcentreId={workcentreId as string}
              handlePrimaryButtonClick={handleDownloadData}
              handleEditCapacityClick={handleEditCapacityClick}
              handleBowWaveClick={handleBowWaveClickHandler}
              downloadButtonButtonText="Download capacity data"
              editCapacityButtonText="Edit capacity data"
              handleAlternativeWorkcentres={handleAlternativeWorkcentres}
              editPlanVolumeButtonText="Edit plan volume"
              isAutoBowWaveCalculating={
                constrainedPlanRunningStatus.current ===
                WORKCENTRE_STATUS.INITIATED
              }
              isCapacityDataDownloading={isCapacityDataDownloading}
              isDataDownloading={isDataDownloading || isLedgerDownloading}
              handleEditSettingsClick={handleEditSettingsClick}
              handleEditProduction={handleEditProduction}
            />
          </Box>
        </Box>
        <Box>
          {dashboardDisplayState.mode === DISPLAY_MODES.DASHBOARD_MODE ? (
            <>
              {dashboardDisplayState.subMode ===
              DASHBOARD_OPTIONS.VOLUME_PROJECTION ? (
                <Box>
                  <VolumeProjectionView
                    planId={planId}
                    workcentreId={workcentreId}
                  />
                </Box>
              ) : null}

              {dashboardDisplayState.subMode === DASHBOARD_OPTIONS.CAPACITY ? (
                <Box>
                  <CapacityPage dashboardDisplay={true} />
                </Box>
              ) : null}
              {dashboardDisplayState.subMode ===
              DASHBOARD_OPTIONS.STOCK_BUILD ? (
                <Box>
                  <StockBuildPage dashboardDisplay={true} />
                </Box>
              ) : null}

              {dashboardDisplayState.subMode ===
              DASHBOARD_OPTIONS.STOCK_PROJECTION ? (
                <Box>
                  <StockProjectionExceptionPage />
                </Box>
              ) : null}

              {dashboardDisplayState.subMode === '' ? (
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <DashboardCardView
                      fullHeight
                      title="Volume projection"
                      onClick={() => {
                        updateDashboardState({
                          type: 'SUB_MODE',
                          value: DASHBOARD_OPTIONS.VOLUME_PROJECTION,
                        })
                      }}
                    >
                      <VolumeChartView />
                    </DashboardCardView>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCardView
                      title="Capacity projection"
                      onClick={() => {
                        updateDashboardState({
                          type: 'SUB_MODE',
                          value: DASHBOARD_OPTIONS.CAPACITY,
                        })
                      }}
                    >
                      <CapacityView />
                    </DashboardCardView>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCardView
                      title="Stock projection exceptions"
                      onClick={() => {
                        updateDashboardState({
                          type: 'SUB_MODE',
                          value: DASHBOARD_OPTIONS.STOCK_PROJECTION,
                        })
                      }}
                    >
                      <StockProjectionView />
                    </DashboardCardView>
                  </Grid>
                  <Grid size={6}>
                    <DashboardCardView
                      title="Inventory projection"
                      onClick={() => {
                        updateDashboardState({
                          type: 'SUB_MODE',
                          value: DASHBOARD_OPTIONS.STOCK_BUILD,
                        })
                      }}
                    >
                      <StockBuildView />
                    </DashboardCardView>
                  </Grid>
                </Grid>
              ) : null}
            </>
          ) : (
            <Box>
              <PlanningBookView />
            </Box>
          )}
        </Box>
      </Box>
      <DownloadDataModal
        keyMeasures={isLedgerSelected ? ledgerCodes : keyMeasures}
        fileNames={hasLedgerDownload ? FILE_NAME_WITH_LEDGER : FILE_NAME}
        keyFigureLabels={isLedgerSelected ? ledgerLabels : undefined}
        isKeyMeasuresLoading={iskeyMeasuresLoading}
        isModalOpen={isModalOpen}
        handleModalSave={handleModalSave}
        handleModalCancel={handleModalCancel}
        dataDownloadSettingsState={dataDownloadSettingsState}
        updateState={updateState}
        fileNameLabel="File name"
        fileVersionLabel="File version"
        keyFiguresLabel="Key figures"
        heading="Download data"
        planTemplateId={planId}
        workcentreId={workcentreId}
      />
      <AlertDialog
        isLoading={workCentreRunStatusLoading}
        condition={workcentreRunStatus?.is_any_workcentre_being_run ?? false}
        handleClose={() => setShowConfirmDialog(false)}
        open={showConfirmDialog}
        handleConfirm={handleBowWaveClick}
      />
    </Page>
  )
}
